import Vue from 'vue';
import PageBase from '@glittr/frontend-core/src/core/v2/app/pageBase';
import PostAutomationContactSearchRequestModel from '@/src/services/v2/model/post-automation-contact-search-request-model';
import PostReactivationFormPayloadRequestModel from '@/src/services/v2/model/post-reactivation-form-payload-request-model';
import GetAutomationFormPayloadRequestModel from '@/src/services/v2/model/get-automation-form-payload-request-model';
import reactivationSchema from './reactivation.vue.model.schema';

interface CheckBoxItem {
  id : string;
  caption:string;
}
interface RadioButtonItem {
  id : string;
  caption:string;
  selected:boolean;
}
export default class AutomationReactivationVM extends PageBase {
  // Widget Configuration to model properties
  contactId: string = '';

  tenantKey: string = 'kls';

  submitRedirectUrl: string = '#';

  submitAndDonateRedirectUrl: string = '#';

  // State properties
  isLoading: boolean = true;

  isContactDataPrefilled: boolean = false;

  isSubmitting: boolean = false;

  isSubmitted: boolean = false;

  error: any;

  showFirstName: boolean = false;

  showLastName: boolean = false;

  // Form properties
  contactDisplayNames: string[] = [];

  formSearchModel = new PostAutomationContactSearchRequestModel();

  formModel = new PostReactivationFormPayloadRequestModel();

  characteristicItems: CheckBoxItem[] = [];

  newsletterItems: CheckBoxItem[] = [];

  donationItems: RadioButtonItem[] = [];

  availableNewsletterOptions: string[] = [];

  availableDonationOptions: string[] = [];

  async initialize(): Promise<void> {
    this.isLoading = true;
    if (this.isContactIdSet) {
      // load contact and do reactivation
      this.formModel.schema = reactivationSchema;
      try {
        await this.initializeFormPayload();
        await this.initializeFormNewsletter();
        await this.initializeFormGiftItems();
        await this.initializeFormDonationItems();
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    } else {
      // show contact search screen
    }
    this.isLoading = false;
  }

  async initializeFormPayload() {
    const req = new GetAutomationFormPayloadRequestModel();
    req.contactId = this.contactId;
    req.appSystemKey = this.tenantKey;

    /// V2 Services: eMail Property makes problems... (email / eMail)
    // -----------------------------------------------------------------
    // extract the dto and inject into new viewmodel to have a 'real' viewmodel that can be sent to backend again
    const backendPayloadVM = await Vue.$service.v2.api.automation.getAutomationFormPayload(req);
    const formPayloadVM = backendPayloadVM.data;
    this.formModel.contact = {};
    if (formPayloadVM !== undefined) {
      if (formPayloadVM !== undefined && formPayloadVM.contactResource !== undefined) {
        if (formPayloadVM.contactResource.street) { Vue.set(this.formModel.contact, 'street', formPayloadVM.contactResource.street); }
        if (formPayloadVM.contactResource.houseNumber) { Vue.set(this.formModel.contact, 'houseNumber', formPayloadVM.contactResource.houseNumber); }
        if (formPayloadVM.contactResource.city) { Vue.set(this.formModel.contact, 'city', formPayloadVM.contactResource.city); }
        if (formPayloadVM.contactResource.postalCode) { Vue.set(this.formModel.contact, 'postalCode', formPayloadVM.contactResource.postalCode); }
        if (formPayloadVM.contactResource.email) { Vue.set(this.formModel.contact, 'email', formPayloadVM.contactResource.email); }
        // only show firstName and Lastname field if the account is not a Mehrpersonenhaushalt and Firstname and Lastname are set
        if (formPayloadVM.contactDisplayNames?.length === 1) {
          if (formPayloadVM.contactResource.firstName) {
            Vue.set(this.formModel.contact, 'firstName', formPayloadVM.contactResource.firstName);
            this.showFirstName = true;
          }
          if (formPayloadVM.contactResource.lastName) {
            Vue.set(this.formModel.contact, 'lastName', formPayloadVM.contactResource.lastName);
            this.showLastName = true;
          }
        }
        if (formPayloadVM.contactDisplayNames) { this.contactDisplayNames = formPayloadVM.contactDisplayNames; }
        this.isContactDataPrefilled = true;
      }
      // newsletter
      if (formPayloadVM.availableNewsletters !== undefined && formPayloadVM.availableNewsletters !== null) {
        this.formModel.newsletterItems = formPayloadVM.selectedNewsletters?.map(String);
      }
      if (formPayloadVM.availableNewsletters !== undefined && formPayloadVM.availableNewsletters !== null) {
        this.availableNewsletterOptions = formPayloadVM.availableNewsletters;
      }
      if (formPayloadVM.availableDonationCycles !== undefined && formPayloadVM.availableDonationCycles !== null) {
        this.availableDonationOptions = formPayloadVM.availableDonationCycles;
      }
    } else {
      this.contactId = '';
      this.isContactDataPrefilled = false;
    }
  }

  async initializeFormNewsletter() {
    this.availableNewsletterOptions.forEach((key) => {
      let keyWithoutSort = key;
      if (key.indexOf('_') > 0) {
        const [sortString, keyString] = key.split('_');
        keyWithoutSort = keyString;
      }
      const option = {
        id: key,
        caption: Vue.$t(`widget.welcome.newsletter.${this.tenantKey}.${keyWithoutSort}`),
      };
      this.newsletterItems.push(option);
    });
  }

  async initializeFormGiftItems() {
    this.characteristicItems = [];
    const gift = {
      id: 'gift',
      caption: Vue.$t(`widget.welcome.characteristic.${this.tenantKey}.gift`),
    } as CheckBoxItem;
    this.characteristicItems.push(gift);
  }

  async initializeFormDonationItems() {
    this.availableDonationOptions.forEach((key) => {
      let keyWithoutSort = key;
      if (key.indexOf('_') > 0) {
        const [sortString, keyString] = key.split('_');
        keyWithoutSort = keyString;
      }
      const option = {
        id: key,
        caption: Vue.$t(`widget.automation.donation.${this.tenantKey}.${keyWithoutSort}`),
        selected: keyWithoutSort === 'regularly',
      };
      this.donationItems.push(option);
      if (option.selected) this.formModel.donationItem = option.id;
    });
  }

  get isContactIdSet(): boolean {
    return this.contactId !== undefined && this.contactId !== '';
  }

  get isContactLoaded(): boolean {
    return this.isContactIdSet && this.isContactDataPrefilled;
  }

  get contactResultDisplay(): string {
    return this.contactDisplayNames.join('<br>');
  }

  async searchContact() {
    this.isLoading = true;
    try {
      if (!this.formSearchModel.validate()) {
        this.isLoading = false;
        return;
      }
      const req = this.formSearchModel;
      const dataRecord = await Vue.$service.v2.api.automation.postAutomationContactSearch(
        req,
      );
      this.contactId = dataRecord.data.toString();
      this.initialize();
      this.isLoading = false;
    } catch (error: any) {
      this.error = error;
    }
    this.isSubmitting = false;
    this.isLoading = false;
  }
  // submit
  get canSubmitForm() { return !this.isSubmitting; }
  async onSubmitClick() {
    if (this.submitRedirectUrl !== undefined && this.submitRedirectUrl !== '') {
      this.submitClick(this.submitRedirectUrl);
    } else {
      this.submitClick('');
    }
  }
  async onSubmitAndRedirectClick() {
    if (this.submitAndDonateRedirectUrl !== undefined && this.submitAndDonateRedirectUrl !== '') {
      this.submitClick(this.submitAndDonateRedirectUrl);
    } else {
      this.onSubmitClick();
    }
  }
  async submitClick(redirectLocation : string) {
    await this.submitForm();
    if (this.isSubmitted === true) {
      if (redirectLocation !== undefined && redirectLocation !== '') {
        window.location.href = redirectLocation;
      }
    }
  }
  async submitForm() {
    this.isSubmitting = true;
    try {
      if (!this.formModel.validate()) {
        this.isSubmitting = false;
        return;
      }
      if (this.formModel.newsletterItems !== undefined && this.formModel.newsletterItems?.length > 0) {
        if (this.formModel.contact.email === undefined || this.formModel.contact.email === '') {
          this.isSubmitting = false;
          this.error = Vue.$t('widget.automation.submitValidationEmailMissing'); // hackyhack: 'E-Mail: Muss ausgefüllt werden';
          return;
        }
      }
      this.formModel.languageIso2 = Vue.$translation.get() ?? Vue.$config.values['language-default'];
      this.formModel.contactId = this.contactId;
      this.formModel.tenantKey = this.tenantKey;
      const req = this.formModel;
      await Vue.$service.v2.api.reactivation.postReactivationFormPayload(req);
      this.isSubmitted = true;
    } catch (error: any) {
      this.error = error;
    }
    this.isSubmitting = false;
  }
}
